import { Howl } from 'howler'

export default defineNuxtPlugin(() => {
  class SoundControl {
    constructor() {
      this.background = new BackgroundSound()
      this.sound = new Sound()
      console.info('[Sound] initialized.')
    }

    mute(state) {
      this.background.mute(state)
      this.sound.mute(state)
    }

    fade(a, b, c) {
      this.background.fade(a, b, c)
      this.sound.fade(a, b, c)
    }

    volume() {
      return this.background.volume()
    }
  }

  const soundControl = new SoundControl()

  if (import.meta.client) {
    window.soundControl = soundControl
  }

  return {
    provide: {
      SoundControl: soundControl,
    },
  }
})

// 基礎音效類別
class BaseSound {
  constructor() {
    this.sounds = new Map()
    this.isMuted = true
    this._initializeSoundConfigs()
  }

  // 由子類別實作
  _initializeSoundConfigs() {
    this.configs = {}
  }

  async load(key) {
    if (!this.configs[key]) {
      console.warn(`Sound "${key}" not found`)
      return null
    }

    if (this.sounds.has(key)) {
      return this.sounds.get(key)
    }

    const config = this.configs[key]

    // 動態載入音效檔案
    const soundModule = await config.loader()
    const sound = new Howl({
      src: [soundModule.default],
      preload: true,
      mute: this.isMuted,
      ...config.options,
    })

    this.sounds.set(key, sound)
    return sound
  }

  mute(state) {
    if (typeof state === 'undefined') {
      return this.isMuted
    }
    this.isMuted = state
    for (const sound of this.sounds.values()) {
      sound.mute(state)
    }
    return this.isMuted
  }

  fade(from, to, duration) {
    for (const sound of this.sounds.values()) {
      sound.fade(from, to, duration)
    }
  }

  stop() {
    for (const sound of this.sounds.values()) {
      sound.stop()
    }
  }
}

// 音效類別（短音效）
class Sound extends BaseSound {
  _initializeSoundConfigs() {
    // 儲存動態 import 函式而非實際檔案
    this.configs = {
      click: {
        loader: () => import('assets/sounds/effect/click.mp3'),
      },
      close: {
        loader: () => import('assets/sounds/effect/close.mp3'),
      },
      favoriteAdd: {
        loader: () => import('assets/sounds/effect/favorite_click.mp3'),
      },
      favoriteRemove: {
        loader: () => import('assets/sounds/effect/favorite_unclick.mp3'),
      },
      mailDefault: {
        loader: () => import('assets/sounds/effect/mail.mp3'),
      },
      mailSystem: {
        loader: () => import('assets/sounds/effect/system_mail.mp3'),
      },
      levelUp: {
        loader: () => import('assets/sounds/effect/lv_up.mp3'),
      },
      openDrawer: {
        loader: () => import('assets/sounds/effect/monster_click.mp3'),
      },
      closeDrawer: {
        loader: () => import('assets/sounds/effect/monster_unclick.mp3'),
      },
    }
  }

  async play(soundType) {
    // 如果已靜音，提前返回，不載入也不播放
    if (this.isMuted) return

    const sound = await this.load(soundType)
    if (!sound) return

    sound.play()
  }
}

// 背景音樂類別（循環播放）
class BackgroundSound extends BaseSound {
  constructor() {
    super()
    this.scene = ''
  }

  _initializeSoundConfigs() {
    // 儲存動態 import 函式而非實際檔案
    this.configs = {
      index: {
        loader: () => import('assets/sounds/background/sign_up.mp3'),
        options: { loop: true },
      },
      lobby: {
        loader: () => import('assets/sounds/background/lobby.mp3'),
        options: { loop: true },
      },
      loading: {
        loader: () => import('assets/sounds/background/loading.mp3'),
        options: { loop: true },
      },
      diamondShop: {
        loader: () => import('assets/sounds/background/dms_bgm.mp3'),
        options: { loop: true },
      },
      jackpot: {
        loader: () => import('assets/sounds/effect/jackpot.mp3'),
        options: { loop: false },
      },
    }
  }

  async play(scene) {
    // 如果提供場景參數，則切換並播放該場景
    if (scene) {
      if (this.scene === scene) return

      // 停止當前場景的音樂
      const currentSound = this.sounds.get(this.scene)
      if (currentSound) {
        currentSound.stop()
      }

      // 更新場景（無論是否靜音）
      this.scene = scene

      // 如果已靜音，不載入也不播放
      if (this.isMuted) return

      // 載入並播放新場景的音樂
      const sound = await this.load(scene)
      if (sound) {
        sound.play()
      }
    } else {
      // 如果沒有參數，播放當前場景
      // 如果已靜音，不播放
      if (this.isMuted) return

      let sound = this.sounds.get(this.scene)
      if (!sound) {
        sound = await this.load(this.scene)
      }
      if (sound) {
        sound.play()
      }
    }
  }

  async setSource(scene, newSource) {
    if (!scene || !newSource || !newSource.src) {
      console.warn('Invalid parameters for setSource')
      return
    }

    // 更新配置：將 src 轉換為 loader 函式
    // 注意：如果 src 是靜態字串，需要建立一個返回該字串的 loader
    // 但在實際使用中，default.vue 傳入的是 CDN URL，所以需要特殊處理
    this.configs[scene] = {
      loader: async () => ({ default: newSource.src }),
      options: {
        loop: true,
        ...newSource.options,
      },
    }

    // 如果該場景的音效已經載入，則需要重新載入
    if (this.sounds.has(scene)) {
      const currentSound = this.sounds.get(scene)
      currentSound.stop()
      currentSound.unload()
      this.sounds.delete(scene)

      // 如果當前正在播放這個場景的音樂，且未靜音，需要重新載入並播放
      if (this.scene === scene && !this.isMuted) {
        const sound = await this.load(scene)
        if (sound) {
          sound.play()
        }
      }
    }
  }

  // 覆寫 stop 方法，只停止當前場景
  stop() {
    const sound = this.sounds.get(this.scene)
    if (sound) {
      sound.stop()
    }
  }

  playing() {
    const sound = this.sounds.get(this.scene)
    return sound ? sound.playing() : false
  }

  pause() {
    const sound = this.sounds.get(this.scene)
    if (sound) {
      sound.pause()
    }
  }

  // 覆寫 fade 方法，只 fade 當前場景
  fade(from, to, duration) {
    const sound = this.sounds.get(this.scene)
    if (sound) {
      sound.fade(from, to, duration)
    }
  }

  volume(val) {
    const sound = this.sounds.get(this.scene)
    if (sound) {
      if (typeof val === 'number') {
        sound.volume(val)
      }
      return sound.volume()
    }
    return 0
  }
}
